import React, { useState } from 'react';
import { HiCalendar, HiLocationMarker, HiTicket, HiUsers } from 'react-icons/hi';
import { EventListItem } from '../../../types';
import { formatEventDateRange } from '../../../utils/dateFormatters';
import { useGeneral } from '../../../contexts';
import GrayBanner from '../../ui/GrayBanner/GrayBanner';
import styles from './CommunityEventCard.module.css';

interface CommunityEventCardProps {
  event: EventListItem;
  onClick?: (event: EventListItem) => void;
  isLast?: boolean;
  showParticipantCount?: boolean;
  className?: string;
  compactMode?: boolean;
  noBorder?: boolean;
}

const CommunityEventCard: React.FC<CommunityEventCardProps> = ({
  event,
  onClick,
  isLast = false,
  showParticipantCount = true,
  className = '',
  compactMode = false,
  noBorder = false
}) => {
  const { language } = useGeneral();
  const { getTranslation, language: currentLanguage } = language;
  const eventTitle = (event.eventTitle || event.title || '').replace(/\\"/g, '"');
  const imageUrl = event.cover || event.bannerImage || event.image || null;
  const hasImage = !!imageUrl;
  const [imageLoading, setImageLoading] = useState(hasImage);
  const location = event.isOnline === "1" 
    ? getTranslation('events.onlineEvent')
    : event.locationAddress || getTranslation('events.locationTBD');
  
  const eventDate = formatEventDateRange(event.eventStartDateTime, event.eventEndDateTime, currentLanguage);
  
  const participantCount = event.participantsCount || event.userCount || event.user_count || event.soldTicketCount || 0 ;
  
  // Determine price display
  const isFree = event.isFree === "1";
  const hasMixedPricing = event.isFree === "3";
  const priceText = isFree 
    ? getTranslation('events.free') || 'Free' 
    : hasMixedPricing
    ? getTranslation('events.free') + '/' + getTranslation('events.paid') || 'Free/Paid'
    : getTranslation('events.paid') || 'Paid';

  const handleClick = () => {
    if (onClick) {
      onClick(event);
    }
  };

  return (
    <div 
      className={`${styles.communityEventCard} ${isLast ? styles.lastCard : ''} ${className} ${compactMode ? styles.compactMode : ''} ${noBorder ? styles.noBorder : ''}`}
      onClick={handleClick}
      role="button"
      tabIndex={0}
    >
      {/* Banner Image on Left with rounded corners and padding */}
      <div className={styles.bannerSection}>
        {hasImage ? (
          <>
            {imageLoading && <div className="image-shimmer" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, borderRadius: 'inherit' }} />}
            <img 
              src={imageUrl} 
              alt={eventTitle}
              className={styles.bannerImage}
              onLoad={() => setImageLoading(false)}
              onError={() => {
                setImageLoading(false);
              }}
            />
          </>
        ) : (
          <GrayBanner className={styles.bannerImage} id={event.eventID} />
        )}
        {compactMode && showParticipantCount && participantCount > 0 && (
          <div className={styles.bannerParticipantCount}>
            {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
            {/* @ts-ignore - react-icons compatibility with React 19 */}
            <HiUsers size={12} />
            <span>{participantCount}</span>
          </div>
        )}
      </div>

      {/* Event Details on Right */}
      <div className={styles.detailsSection}>
        <div className={styles.eventDetails}>
          <div className={styles.eventDate}>
            {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
            {/* @ts-ignore - react-icons compatibility with React 19 */}
            <HiCalendar size={16} />
            <span>{eventDate}</span>
          </div>
          <h3 className={styles.eventTitle}>
            {eventTitle}
          </h3>
          {!compactMode && (
            <>
              <div className={styles.eventLocation}>
                {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
                {/* @ts-ignore - react-icons compatibility with React 19 */}
                <HiLocationMarker size={16} />
                <span>{location}</span>
              </div>
              <div className={styles.eventActions}>
                {showParticipantCount && participantCount > 0 && (
                  <div className={styles.participantCount}>
                    {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
                    {/* @ts-ignore - react-icons compatibility with React 19 */}
                    <HiUsers size={14} />
                    <span>{participantCount} {getTranslation('events.participants') || 'Katılımcı'}</span>
                  </div>
                )}
                <div className={styles.priceBadge}>
                  {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
                  {/* @ts-ignore - react-icons compatibility with React 19 */}
                  <HiTicket size={14} />
                  <span>{priceText}</span>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CommunityEventCard;

